/**
 * FilterSelect — Phase 41 Wave B.
 *
 * Shared dropdown filter component used everywhere in the app
 * (admin tabs + user-side ETF picker). Centralises three concerns
 * that were duplicated and inconsistent across 5+ tabs:
 *
 *   1. ALPHABETICAL SORT. Options are sorted case-insensitively
 *      regardless of what the backend returns. Pass `sort={false}`
 *      to opt out (e.g. fixed taxonomy buckets with a "natural" order).
 *
 *   2. HTML STRIPPING. JustETF (and other 3rd-party scrapers) sometimes
 *      leak `<br />` / `&nbsp;` into facet values. We strip them on
 *      DISPLAY only — the underlying value (sent back to the parent)
 *      stays untouched so round-tripping with the backend keeps working.
 *
 *   3. CONSISTENT STYLE. zinc-950 bg + zinc-800 border + blue-500 focus.
 *      One look across the whole app so users learn it once.
 *
 * The component also supports:
 *   • `placeholder`         — the empty-state label shown as the first
 *                             option (value = `placeholderValue`, default "").
 *   • `placeholderValue`    — sentinel used when nothing is selected.
 *                             Some legacy filters use "all" instead of "".
 *   • `unknownLabel`        — optional trailing entry for values that
 *                             couldn't be classified (value = "_unknown").
 *   • `label`               — optional inline uppercase label rendered to
 *                             the left of the select.
 *   • `options`             — array of either strings or {value,label}.
 */
import { forwardRef } from "react";

const HTML_TAG_RE = /<[^>]+>/g;

/** Returns the option in {value,label} shape. Strips HTML from the label. */
function normaliseOption(opt) {
  if (opt == null) return null;
  let value, label;
  if (typeof opt === "string" || typeof opt === "number") {
    value = String(opt);
    label = value;
  } else if (typeof opt === "object") {
    value = opt.value;
    label = opt.label ?? opt.value;
  }
  if (value == null || value === "") return null;
  if (typeof label === "string") {
    label = label
      .replace(HTML_TAG_RE, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!label) label = String(value);
  }
  return { value: String(value), label: String(label) };
}

function compareLabel(a, b) {
  return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
}

export const FilterSelect = forwardRef(function FilterSelect(
  {
    value,
    onChange,
    options = [],
    placeholder = "All",
    placeholderValue = "",
    testid,
    className = "",
    width = "max-w-[180px]",
    unknownLabel,
    unknownValue = "_unknown",
    sort = true,
    disabled = false,
    label,
    labelClassName = "",
    id,
    /* Phase 2: "field" opts into the 40px product control height (A5). The
       default keeps the historical dense look, so the Admin Console — which
       shares this component — is not resized. */
    size = "dense",
    ...rest
  },
  ref,
) {
  const normalised = options
    .map(normaliseOption)
    .filter(Boolean);
  if (sort) normalised.sort(compareLabel);

  const sizeClass = size === "field"
    ? "h-[var(--control-h-field)] px-2.5 text-sm rounded-md"
    : "py-1.5 px-2 text-xs rounded";

  const select = (
    <select
      ref={ref}
      id={id}
      data-testid={testid}
      value={value == null ? placeholderValue : value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={
        "bg-surface-subtle border border-line text-content " +
        "focus:outline-none focus:border-focus focus-visible:ring-2 focus-visible:ring-focus/30 " +
        "disabled:opacity-40 disabled:cursor-not-allowed " +
        `${sizeClass} ${width} ${className}`
      }
      {...rest}
    >
      {placeholder !== null && (
        <option value={placeholderValue}>{placeholder}</option>
      )}
      {normalised.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
      {unknownLabel && (
        <option value={unknownValue}>{unknownLabel}</option>
      )}
    </select>
  );

  if (!label) return select;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={
          "text-label uppercase text-content-muted tracking-wide " + labelClassName
        }
      >
        {label}
      </span>
      {select}
    </span>
  );
});
